// src/components/ConfirmationModal.js
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../hooks';
import Button from './Button';
import styles from './ConfirmationModal.module.css';

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  message, 
  title,
  confirmText,
  cancelText,
  isLoading = false
}) => {
  const { t } = useTranslation();
  
  // Focus trap for modal accessibility
  const modalRef = useFocusTrap(isOpen, {
    initialFocus: true,
    restoreFocus: true
  });

  // Handle ESC key
  useEffect(() => {
    if (isOpen && onClose) {
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  // Prevent clicks inside the modal from closing it
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return createPortal(
    <div 
      className={styles.modalOverlay} 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className={styles.modalContainer} onClick={handleModalClick} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 id="modal-title" className={styles.modalTitle}>
            {title || t('common.confirm')}
          </h3>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label={t('common.close')}
          >
            &times;
          </button>
        </div>
        
        <div className={styles.modalContent}>
          <p>{message}</p>
        </div>
        
        <div className={styles.modalActions}>
          <Button 
            onClick={onClose} 
            variant="secondary"
            disabled={isLoading}
          >
            {cancelText || t('common.cancel')}
          </Button>
          <Button 
            onClick={onConfirm} 
            variant="danger"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className={styles.spinner}></span>
            ) : (
              confirmText || t('common.confirm')
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationModal;